/**
 * Season/week helpers for the Gamebook tab's picker (`SeasonWeekPicker.tsx`) — pure, no I/O. The
 * server never needs to enumerate these (they're a fixed NFL structure, not per-org data); mirrors
 * `apps/customer-api/src/services/gamebookTeams.ts`'s `nflWeekSkeleton` (kept as a SEPARATE small
 * copy rather than a shared package purely because there's no existing frontend/backend-shared
 * "domain constants" package in this repo to put it in — see that file for the canonical version).
 */

const POST_NAMES: Record<string, string> = {
  POST01: "Wild Card",
  POST02: "Divisional",
  POST03: "Conf. Championship",
  POST04: "Super Bowl",
};

export function weekLabel(week: string): string {
  if (week === "PRE00") {
    return "Hall of Fame";
  }
  if (POST_NAMES[week]) {
    return POST_NAMES[week];
  }
  const m = /^([A-Z]+)(\d+)$/.exec(week);
  if (!m) {
    return week;
  }
  const n = parseInt(m[2], 10);
  if (m[1] === "PRE") {
    return `Preseason Wk ${n}`;
  }
  if (m[1] === "POST") {
    return `Postseason Wk ${n}`;
  }
  return `Week ${n}`;
}

export interface WeekGroup {
  label: string;
  weeks: string[];
}

/** Every selectable week, grouped for the `Select`'s optgroup-style rendering. */
export function weekGroups(): WeekGroup[] {
  const reg: string[] = [];
  for (let i = 1; i <= 18; i++) {
    reg.push(`REG${String(i).padStart(2, "0")}`);
  }
  return [
    { label: "Preseason", weeks: ["PRE00", "PRE01", "PRE02", "PRE03"] },
    { label: "Regular Season", weeks: reg },
    { label: "Postseason", weeks: ["POST01", "POST02", "POST03", "POST04"] },
  ];
}

/** Season options — the server's `currentSeason` plus the two prior seasons, matching how far
 *  back `master_play_by_play` is realistically expected to have data. `currentSeason` itself
 *  already rolls over to the upcoming season as soon as the prior one ends (see
 *  `estimateCurrentSeasonWeek`'s doc comment), months before that season's data exists — no need
 *  to list a season further out than that here. */
export function seasonOptions(currentSeason: number): number[] {
  return [currentSeason, currentSeason - 1, currentSeason - 2];
}

/** Default week within a season: the current NFL week when `season` is the current season,
 *  otherwise that season's final regular-season week (mirrors the reference mock's
 *  `defaultWeek()` fallback for a season with no more-specific signal). */
export function defaultWeekFor(
  season: number,
  currentSeason: number,
  currentWeek: string
): string {
  return season === currentSeason ? currentWeek : "REG18";
}
